import "../styles/globals.css";
import "@fortawesome/fontawesome-svg-core/styles.css";
import "../styles/algolia-overrides.scss";

import type { AppProps } from "next/app";
import "@dreambigger/design-system/src/styles/index.scss";
import { DefaultThemeProvider, Spin } from "@dreambigger/design-system";
import InspireLayout from "@dreambigger/shared/src/components/layout/inspire-layout";
import {
  api,
  communityApi,
  orgApi,
  tokenHelper,
} from "@dreambigger/shared/src/api/weserve/api-helper";

import { AuthProvider } from "@dreambigger/shared/src/components/auth";
import { Community, Event, OrgBootstrap } from "@dreambigger/shared/src/types";
import { useEffect, useMemo, useState } from "react";
import { FooterCta } from "@dreambigger/communities/src/components";
import "nprogress/nprogress.css";
import NProgress from "nprogress";
import { Router } from "next/router";
import useMixpanel from "@dreambigger/shared/src/hooks/use-mixpanel";
import { ShareStoryFooter } from "@dreambigger/inspire-events/src/components/ui";

NProgress.configure({ showSpinner: false });
Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

// A list of pathnames that we don't want to be wrapped with inspire wrappers, such as 404 pages.
const skipWrappersArr = ["/404"];

// Routes that resolve community/orgBootstrap client-side from the JWT instead of via
// getStaticProps/getServerSideProps. The layout fetches the context here so the page
// still renders inside InspireLayout (same header/footer as other pages).
const clientContextRoutes = ["/user/events"];

// Key used to stash the most recently viewed community slug so client-context routes
// can keep that branding instead of falling back to the user's home community from JWT.
const LAST_COMMUNITY_SLUG_KEY = "inspire:lastCommunitySlug";

// TTL on the cached slug so a stale value can't bleed across communities after a long
// idle. 30 minutes balances "remember branding across navigation" with "don't show
// stale branding to a user returning from a long break."
const LAST_COMMUNITY_SLUG_TTL_MS = 30 * 60 * 1000;

const writeLastCommunitySlug = (slug: string) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      LAST_COMMUNITY_SLUG_KEY,
      JSON.stringify({ slug, ts: Date.now() })
    );
  } catch {
    // sessionStorage may be disabled — non-fatal
  }
};

const readLastCommunitySlug = (): string | null => {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(LAST_COMMUNITY_SLUG_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { slug?: string; ts?: number };
    if (
      !parsed?.slug ||
      typeof parsed.ts !== "number" ||
      Date.now() - parsed.ts > LAST_COMMUNITY_SLUG_TTL_MS
    ) {
      sessionStorage.removeItem(LAST_COMMUNITY_SLUG_KEY);
      return null;
    }
    return parsed.slug;
  } catch {
    sessionStorage.removeItem(LAST_COMMUNITY_SLUG_KEY);
    return null;
  }
};

function MyApp({ Component, pageProps, router }: AppProps) {
  const isFixed =
    (pageProps?.story?.images && pageProps?.story?.images.length > 0) ||
    undefined;

  const isClientContextRoute = clientContextRoutes.includes(router.pathname);
  const [clientCommunity, setClientCommunity] =
    useState<Community | null>(null);
  const [clientOrgBootstrap, setClientOrgBootstrap] =
    useState<OrgBootstrap | null>(null);

  // Remember the most recently viewed community slug so client-context routes
  // (e.g. /user/events) can keep the same branding across navigation.
  useEffect(() => {
    const slug = (pageProps.community as Community | undefined)?.slug;
    if (slug) writeLastCommunitySlug(slug);
  }, [pageProps.community]);

  // For routes that resolve community client-side, prefer the last-viewed community
  // slug (so the header keeps its branding across navigation) and fall back to the
  // JWT's home community slug. If the cached slug fails (stale, deleted community,
  // etc.) retry with the JWT slug rather than leaving the user on an infinite Spin.
  useEffect(() => {
    if (!isClientContextRoute) return;
    const lastSlug = readLastCommunitySlug();
    const jwtSlug = tokenHelper.getCommunitySlug();
    const initialSlug = lastSlug || jwtSlug;
    if (!initialSlug) return;
    let cancelled = false;

    const loadContext = async (slug: string) => {
      const communityRes = await communityApi.getBySlug(slug);
      if (cancelled) return;
      const communityData = communityRes.data;
      setClientCommunity(communityData);
      const orgRes = await orgApi.getOrgBootstrap(
        communityData.organization.id
      );
      if (cancelled) return;
      setClientOrgBootstrap(orgRes.data);
    };

    (async () => {
      try {
        await loadContext(initialSlug);
      } catch (err) {
        if (cancelled) return;
        console.error(
          "Unable to load community context for slug:",
          initialSlug,
          err
        );
        // Cached slug pointed at something we can't load — drop it and retry
        // with the JWT slug if it differs.
        if (initialSlug !== jwtSlug && jwtSlug) {
          if (typeof window !== "undefined") {
            sessionStorage.removeItem(LAST_COMMUNITY_SLUG_KEY);
          }
          try {
            await loadContext(jwtSlug);
          } catch (fallbackErr) {
            if (cancelled) return;
            console.error(
              "Fallback to JWT community slug also failed:",
              fallbackErr
            );
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isClientContextRoute, router.pathname]);

  const community = (pageProps.community || clientCommunity || undefined) as
    | Community
    | undefined;
  const event = pageProps.event as Event | undefined;
  const communityColors = community?.meta?.brand?.colors;

  const mixpanel = useMixpanel();

  useEffect(() => {
    // Initialize mixpanel
    mixpanel.init();

    // Register the Company Name and orgId mixpanel properties that should be apart of each mixpanel call.
    if (community) {
      mixpanel.register({
        "Organization Name": community.organization.name,
        "Organization Id": community.organization.id,
        // Legacy variables
        "Company Name": community.organization.name,
        orgId: community.organization.id,
      });
    }
  }, [community]);

  // FOOTER
  // * All pages have a footer, but on some pages and situations within pages, there is also a prefooter.
  //   Example: On event and fundraiser detail pages, there is a prefooter if no stories have been shared yet - <ShareStoryFooter>.
  // * Prefooters can be added to pages by specifying their urls in the switch below.
  const preFooterContent = useMemo(() => {
    // First, check if there is metadata for a normal footer.
    const communityFooter = community?.meta?.footerBanner ? (
      <FooterCta
        ctaText={community.meta.footerBanner.title}
        linkText={community.meta.footerBanner.ctaTitle}
        image={community.meta.footerBanner.image}
        ctaUrl={community.meta.footerBanner.ctaAction}
        background={community.meta.footerBanner.backgroundColor}
      />
    ) : null;

    // If there is, check if a prefooter should be added to it.
    switch (true) {
      case router.pathname.startsWith("/community/[communitySlug]/"):
        if (!community?.meta?.footerBanner) return null;

        return communityFooter;
      case router.pathname.startsWith("/events/[eventId]/detail"):
        if (event && event.storyCount === 0) {
          return <ShareStoryFooter event={event} />;
        }
        if (!community?.meta?.footerBanner) return null;

        return communityFooter;
      default:
        return null;
    }
  }, [community, event]);

  // Enrich orgBootstrap with org meta gtmContainerId
  const resolvedOrgBootstrap = pageProps.orgBootstrap || clientOrgBootstrap;
  const orgBootstrap = {
    ...resolvedOrgBootstrap,
    gtmContainerId: community?.organization?.meta?.gtmContainerId,
  };

  if (skipWrappersArr.includes(router.pathname)) {
    // We don't need wrappers for these pages so just return the page.
    return <Component {...pageProps} />;
  }

  if (!resolvedOrgBootstrap || !community) {
    return <Spin style={{ width: "100%", marginTop: 150 }} />;
  }

  return (
    <AuthProvider api={api} tokenHelper={tokenHelper}>
      <DefaultThemeProvider colors={communityColors}>
        <InspireLayout
          orgBootstrap={orgBootstrap}
          fixed={isFixed}
          preFooterContent={preFooterContent}
        >
          <Component
            {...pageProps}
            community={pageProps.community || clientCommunity}
            orgBootstrap={pageProps.orgBootstrap || clientOrgBootstrap}
          />
        </InspireLayout>
      </DefaultThemeProvider>
    </AuthProvider>
  );
}
export default MyApp;
